import { createFileRoute } from "@tanstack/react-router";
import { Building2, KeyRound, Layers, ScrollText, ShieldCheck } from "lucide-react";
import { Permission, useCan } from "@/components/session-provider";
import { AuditLogPanel } from "@/components/admin/AuditLogPanel";
import { PermissionsPanel } from "@/components/admin/PermissionsPanel";
import { RolesPanel } from "@/components/admin/RolesPanel";
import { SitesPanel } from "@/components/admin/SitesPanel";
import { TEAM_TABS, TeamsPanel, type TeamTab } from "@/components/admin/TeamsPanel";
import { cn } from "@/lib/utils";

/** A dashed GUID, which is what every id in this app's URLs is. */
const GUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * ⚠️ The team and its tab live here rather than in `TeamsPanel`, because a route owns its
 * own search parameters — the panel reads them with `from: "/admin"`. Both are rendering
 * decisions: every panel behind them re-checks server-side.
 */
interface AdminSearch {
  tab?: TabId;
  team?: string;
  teamTab?: TeamTab;
}

export const Route = createFileRoute("/admin")({
  /**
   * ⚠️ Shape only, and it is not a gate. `allowed` below still decides what this person may
   * open, per tab and per permission, and every panel's endpoints re-check server-side — so
   * naming a tab in the URL reaches nothing that clicking it would not.
   */
  validateSearch: (search: Record<string, unknown>): AdminSearch => {
    const result: AdminSearch = {};

    if (typeof search.tab === "string" && TAB_IDS.includes(search.tab as TabId)) {
      result.tab = search.tab as TabId;
    }

    // ⚠️ Shape only, and an id rather than a name — the rule the queue's filters follow, for
    // the same reason: a team's name has no business in a link somebody pastes into a chat
    // window. A team this administrator cannot reach simply is not in the list the panel
    // resolves against, so it selects nothing rather than erroring.
    if (typeof search.team === "string" && GUID.test(search.team)) result.team = search.team;

    if (typeof search.teamTab === "string" && TEAM_TABS.includes(search.teamTab as TeamTab)) {
      result.teamTab = search.teamTab as TeamTab;
    }

    return result;
  },
  component: AdminPage,
});

// ⚠️ No People tab. It moved to /people in the main navigation — Admin configures the
// SHAPE of the system, and looking up a colleague is not that. Left as a comment rather
// than silently deleted, because "where did People go" is the first question anyone who
// knew the old layout will ask.
const tabs = [
  { id: "roles", label: "Roles", icon: ShieldCheck, requires: Permission.RoleManage },
  { id: "permissions", label: "Permissions", icon: KeyRound, requires: Permission.RoleManage },

  // One tab, not two. Teams, their categories and their members were separate screens, and
  // the split was actively harmful: membership IS clearance, so an administrator could add
  // somebody to EMR without the word "Clinical" appearing anywhere on the screen they were
  // looking at. Now the clearance a team carries is above the control that grants it.
  //
  // ⚠️ `requires` is the NARROWER of the two permissions behind this tab; the real test is
  // in `allowed` below, which admits either. It exists for the icon list alone and nothing
  // reads it as a gate.
  { id: "teams", label: "Support Teams", icon: Layers, requires: Permission.TeamManage },

  { id: "sites", label: "Sites", icon: Building2, requires: Permission.SettingsManage },
  { id: "audit", label: "Activity log", icon: ScrollText, requires: Permission.AuditRead },
] as const;

type TabId = (typeof tabs)[number]["id"];

const TAB_IDS: readonly TabId[] = tabs.map((t) => t.id);

function AdminPage() {
  const can = {
    users: useCan(Permission.UserManage),
    roles: useCan(Permission.RoleManage),
    teams: useCan(Permission.TeamManage),
    categories: useCan(Permission.CategoryManage),
    audit: useCan(Permission.AuditRead),
    settings: useCan(Permission.SettingsManage),
  };

  // Each tab against its own permission. The chained ternary this replaced fell through to
  // `can.teams` for anything unrecognised, so a new tab was visible to team managers by
  // default — which is the wrong direction for a list to fail in.
  const allowed: Record<TabId, boolean> = {
    roles: can.roles,

    // Reading the catalog is not privileged in itself — it describes the system, not any
    // record. It sits behind role.manage because the only reason to open it is to decide
    // what a role should carry, and that is the permission for doing so.
    permissions: can.roles,

    // ⚠️ EITHER permission opens it, because the screen holds two separately-granted things:
    // teams and membership under team.manage, categories under category.manage. It was gated
    // on team.manage alone, so a holder of category.manage and nothing else could not reach
    // the only screen where categories exist — and a holder of team.manage alone was shown
    // every category control and refused by the server on each. The panel now writes each
    // half under its own permission and says so where it is read-only.
    teams: can.teams || can.categories,

    sites: can.settings,
    audit: can.audit,
  };

  const visibleTabs = tabs.filter((t) => allowed[t.id]);

  /**
   * Which tab is open, from the URL.
   *
   * ⚠️ This was `useState`, so administration was one address however deep into it somebody
   * was: "look at the Activity log" could not be sent as a link, and a reload — which happens
   * on every quiet session refresh — dropped whoever was mid-task back to the first tab.
   *
   * ⚠️ Falls back to the first tab this person may actually open rather than trusting the
   * value. A tab named in a link that the reader does not hold the permission for is not
   * rendered empty and is not an error; they land where they would have landed anyway. The
   * panels behind each one re-check server-side regardless, so this decides drawing only.
   */
  const search: { tab?: TabId } = Route.useSearch();
  const navigate = Route.useNavigate();

  const requested = search.tab;

  const tab: TabId = requested && allowed[requested] ? requested : (visibleTabs[0]?.id ?? "roles");

  /** Replaces rather than pushes: moving between tabs is not a history trail. */
  const setTab = (next: TabId) => void navigate({ search: { tab: next }, replace: true });

  if (visibleTabs.length === 0) {
    return (
      <div className="mx-auto max-w-lg px-6 py-16 text-center">
        <ShieldCheck className="mx-auto h-10 w-10 text-muted-foreground" aria-hidden />
        <h1 className="mt-4 text-xl font-semibold">Admin</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          You do not have access to any administrative area.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Admin</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Access is granted here, in the application — never from an Entra group.
        </p>
      </header>

      <div className="mt-6 -mx-4 overflow-x-auto border-b border-border px-4 pb-3 sm:mx-0 sm:px-0">
        <div className="flex w-max gap-2">
          {visibleTabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                aria-current={tab === t.id ? "page" : undefined}
                className={cn(
                  "inline-flex h-9 shrink-0 items-center gap-2 rounded-lg px-3 text-sm transition",
                  tab === t.id
                    ? "bg-secondary text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <Icon className="h-4 w-4" aria-hidden />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6">
        {tab === "roles" && <RolesPanel />}
        {tab === "permissions" && <PermissionsPanel onOpenRoles={() => setTab("roles")} />}
        {tab === "teams" && <TeamsPanel />}
        {tab === "sites" && <SitesPanel />}
        {tab === "audit" && <AuditLogPanel />}
      </div>
    </div>
  );
}
